import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useCallback, useRef } from 'react';

/**
 * Store mirror for an outreach email composer, with the first-emission guard.
 *
 * Lexical re-serializes the seeded content on hydrate, so the editor's FIRST
 * emission after a (re)mount is its own seed, not a user edit. When that seed
 * is the PREPARED template (store untouched), it must be registered as the
 * "original" body so the untouched guard works and a language switch can
 * re-seed. But when the store already carries a TOUCHED body at mount (a
 * return from another wizard step remounts the composer on the edited
 * snapshot), the first emission is the hydration of the EDITED body —
 * registering it as the original would silently mark the editor untouched,
 * hide save-as-template, and let a language switch throw the edits away.
 *
 * Shared by the sponsored compose module and the affiliation email step so the
 * semantics can never drift between them.
 */
export const useSeedEmissionGuard = () => {
  const messageSnapshot = useInvitationStoreSelector((state) => state.messageSnapshot);
  const originalTemplate = useInvitationStoreSelector((state) => state.originalTemplate);
  const setMessageSnapshot = useInvitationStoreSelector((state) => state.setMessageSnapshot);
  const setOriginalTemplate = useInvitationStoreSelector((state) => state.setOriginalTemplate);

  const isEditorUntouched = !messageSnapshot || messageSnapshot === originalTemplate;

  // Captured once at mount: whether the store already held edits.
  const touchedAtMountRef = useRef(!isEditorUntouched);
  // Last composer key whose seed emission was consumed.
  const lastSeedKeyRef = useRef<string | null>(null);

  /**
   * Mirror one composer emission into the store. `snapshot` is the Lexical
   * state JSON, or null after a reset (mapped to '' — falsy, so the untouched
   * guard reads it as clean). `composerKey` identifies the current composer
   * mount (language + selected template entry).
   */
  const registerEmission = useCallback(
    (snapshot: string | null, composerKey: string) => {
      // First emission over a body that was already touched at mount: consume
      // the key without touching the original — it is the edited body.
      if (touchedAtMountRef.current && lastSeedKeyRef.current === null) {
        lastSeedKeyRef.current = composerKey;
      }
      const nextSnapshot = snapshot ?? '';
      setMessageSnapshot(nextSnapshot);
      if (snapshot !== null && lastSeedKeyRef.current !== composerKey) {
        lastSeedKeyRef.current = composerKey;
        setOriginalTemplate(nextSnapshot);
      }
    },
    [setMessageSnapshot, setOriginalTemplate]
  );

  return { isEditorUntouched, messageSnapshot, registerEmission };
};
